import * as React from "react";
import { useQuery } from "convex/react";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { ArrowLeft, Coffee } from "lucide-react";
import { Link } from "react-router-dom";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserProfile } from "@/hooks/useUserProfile";
import { api } from "@/lib/convexApi";
import { formatCurrency } from "@/lib/format";
import { BillStatusBadge } from "../bill-shared";

interface AciRollupLine {
  billId: string;
  invoiceNumber?: string;
  vendorName?: string;
  billStatus: string;
  memo: string;
  amount: number;
  createdAt: number;
}

interface AciRollupGroup {
  cargoId?: string;
  aciCode: string;
  productName?: string;
  cargoStatus?: string;
  totalAmount: number;
  lines: AciRollupLine[];
}

type AciRollupRow = AciRollupGroup & { id: string };

function ExpandedLines({ row }: { row: Row<AciRollupRow> }) {
  return (
    <div className="space-y-1 px-3 py-2">
      {row.original.lines.map((line, index) => (
        <div
          key={`${line.billId}-${index}`}
          className="flex items-center justify-between gap-3 text-sm"
        >
          <span className="flex min-w-0 items-center gap-2">
            <Link
              to={`/admin/accounting/bills/${line.billId}`}
              className="shrink-0 font-medium hover:underline"
            >
              {line.invoiceNumber ?? "(no invoice #)"}
            </Link>
            {line.vendorName && (
              <span className="text-muted-foreground shrink-0">
                {line.vendorName}
              </span>
            )}
            {line.memo && (
              <span className="text-muted-foreground min-w-0 truncate text-xs">
                — {line.memo}
              </span>
            )}
            <BillStatusBadge status={line.billStatus} />
          </span>
          <span className="shrink-0 tabular-nums">
            {formatCurrency(line.amount)}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Ledger-style view of coded landed cost per ACI (coffee purchase): every
 * bill line coded to an ACI, with the Overhead Expense bucket last. Voided
 * bills are excluded server-side.
 */
export default function AccountingBillsAciPage() {
  const { authUserId } = useUserProfile();

  const groups = useQuery(
    api.accountingBillAci.getAciRollup,
    authUserId ? { authUserId } : "skip",
  ) as AciRollupGroup[] | undefined;

  const rows = React.useMemo<AciRollupRow[]>(
    () =>
      (groups ?? []).map((group) => ({
        ...group,
        id: group.cargoId ?? "overhead",
      })),
    [groups],
  );

  const columns = React.useMemo<ColumnDef<AciRollupRow>[]>(
    () => [
      {
        accessorKey: "aciCode",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="ACI" />
        ),
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.cargoId
              ? `ACI ${row.original.aciCode}`
              : row.original.aciCode}
          </span>
        ),
      },
      {
        accessorKey: "productName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Coffee" />
        ),
        cell: ({ row }) =>
          row.original.productName ?? (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: "cargoStatus",
        size: 120,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Cargo status" />
        ),
        cell: ({ row }) =>
          row.original.cargoStatus ? (
            <span className="text-muted-foreground text-xs">
              {row.original.cargoStatus.replace(/_/g, " ")}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "lineCount",
        accessorFn: (row) => row.lines.length,
        size: 100,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Bill lines" />
        ),
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.lines.length}</span>
        ),
      },
      {
        accessorKey: "totalAmount",
        size: 130,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label="Total coded"
            className="justify-end"
          />
        ),
        cell: ({ row }) => (
          <span className="block text-right font-medium tabular-nums">
            {formatCurrency(row.original.totalAmount)}
          </span>
        ),
      },
    ],
    [],
  );

  const totalCoded = React.useMemo(
    () => (groups ?? []).reduce((total, group) => total + group.totalAmount, 0),
    [groups],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="sm" className="-ml-2 px-2" asChild>
              <Link to="/admin/accounting/bills">
                <ArrowLeft className="mr-1 h-4 w-4" />
                Bills
              </Link>
            </Button>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">ACI costs</h1>
          <p className="text-muted-foreground mt-1">
            Expenses coded per coffee purchase (ACI) across all bills —
            trucking, storage, steamship, insurance and the rest of the landed
            cost. Bills without a direct ACI sit in Overhead Expense.
          </p>
        </div>
        {groups !== undefined && groups.length > 0 && (
          <span className="text-muted-foreground text-sm">
            {groups.length} ACI{groups.length === 1 ? "" : "s"} ·{" "}
            {formatCurrency(totalCoded)} coded
          </span>
        )}
      </div>

      {groups === undefined ? (
        <div className="space-y-1.5 rounded-md border p-3">
          <Skeleton className="h-8 w-full" />
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-7 w-full" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-md border px-8 py-12 text-center">
          <Coffee className="text-muted-foreground h-8 w-8" />
          <p className="text-foreground font-medium">No ACI coding yet</p>
          <p className="text-muted-foreground text-sm">
            Open a bill and add ACI cost coding to see landed costs per coffee
            here.
          </p>
        </div>
      ) : (
        <InternalEntityDataTable
          data={rows}
          columns={columns}
          pageSize={50}
          enableSelection={false}
          expandedRow={{
            render: ExpandedLines,
            getCanExpand: (row) => row.lines.length > 0,
          }}
        />
      )}
    </div>
  );
}
